<template>
  <div>
    <h1 class="mb-8 font-bold text-3xl">{{__('Users')}}</h1>
    <div
      v-if="inviteFeedback"
      class="mb-6 p-4 rounded flex items-start justify-between gap-4"
      :class="inviteFeedback.type === 'success' ? 'bg-green-100 text-green-900' : 'bg-red-100 text-red-900'"
      role="status"
    >
      <span class="text-sm font-medium">{{ inviteFeedback.message }}</span>
      <button type="button" class="text-sm underline flex-shrink-0" @click="clearInviteFeedback">{{ __('Dismiss') }}</button>
    </div>
    <div class="mb-6 flex flex-wrap justify-between items-center gap-4">
      <search-filter v-model="form.search" class="w-full max-w-md flex-grow" @reset="reset">
        <label class="block text-gray-700">Trashed:</label>
        <select v-model="form.trashed" class="mt-1 w-full form-select">
          <option :value="null" />
          <option value="with">With Trashed</option>
          <option value="only">Only Trashed</option>
        </select>
      </search-filter>
      <div class="flex flex-wrap items-center justify-end gap-2 ml-auto">
        <button type="button" class="btn-indigo" v-on:click="sendJoinEmailAll()" v-if="isOwnerAccount">Send Invite to All</button>
        <inertia-link v-if="isOwnerAccount" class="btn-indigo" :href="route('owner.quick-users.create')">{{ __('Quick add user') }}</inertia-link>
        <inertia-link class="btn-indigo" :href="route('contacts.create')">
          <span>{{__('Create')}}</span>
          <span class="hidden md:inline">{{__('User')}}</span>
        </inertia-link>
      </div>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <!-- <th class="px-6 pt-6 pb-4">Ref. No</th> -->
          <th class="px-6 pt-6 pb-4">{{__('Name')}}</th>
          <th class="px-6 pt-6 pb-4">{{__('Bakery Name')}}</th>
          <th class="px-6 pt-6 pb-4">{{__('City')}}</th>
          <th class="px-6 pt-6 pb-4">{{__('Email')}}</th>
          <th class="px-6 pt-6 pb-4">{{__('Postal code')}}</th>
          <th class="px-6 pt-6 pb-4">{{__('View')}}</th>
        </tr>
        <tr v-for="contact in contacts.data" :key="contact.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
           <!-- <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('contacts.edit', contact.id)">
              {{ contact.ref_no }}
              <icon v-if="contact.deleted_at" name="trash" class="flex-shrink-0 w-3 h-3 fill-gray-400 ml-2" />
            </inertia-link>
          </td> -->
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('contacts.edit', contact.id)">
              {{ contact.name }}
              <icon v-if="contact.deleted_at" name="trash" class="flex-shrink-0 w-3 h-3 fill-gray-400 ml-2" />
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center" :href="route('contacts.edit', contact.id)" tabindex="-1">
              <div v-if="contact.organization">
                {{ contact.business_name }}
              </div>
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center" :href="route('contacts.edit', contact.id)" tabindex="-1">
              {{ contact.city }}
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center" :href="route('contacts.edit', contact.id)" tabindex="-1">
              {{ contact.email }}
            </inertia-link>
          </td>
          <td class="border-t">
            <inertia-link class="px-6 py-4 flex items-center" :href="route('contacts.edit', contact.id)" tabindex="-1">
              {{ contact.postal_code }}
            </inertia-link>
          </td>
          <td class="border-t w-px">
            <inertia-link class="px-4 flex items-center" :href="route('contacts.edit', contact.id)" tabindex="-1" title="View User">
              <icon name="view" class="block w-6 h-6 fill-indigo-400" />
            </inertia-link>
          </td>
          <td class="border-t w-px px-2" v-if="isOwnerAccount">
            <button type="button" class="btn-indigo whitespace-nowrap text-sm" @click="openPasswordModal(contact)">{{ __('Reset Password') }}</button>
          </td>
          <td class="border-t w-px px-2" v-if="isOwnerAccount || $page.props.auth.user.customer_id != 0">
            <button type="button" class="btn-indigo whitespace-nowrap text-sm" @click="sendJoinEmail(contact.id)">Send Invite</button>
          </td>
          <td class="border-t w-px" v-if="$page.props.auth.user.owner">
            <inertia-link class="btn-indigo" :href="route('creditnotes.create', contact.id)" v-if="$page.props.auth.user.owner">
              <span>Create</span>
              <span class="hidden md:inline">Credit Note</span>
            </inertia-link>
          </td>
        </tr>
        <tr v-if="contacts.data.length === 0">
          <td class="border-t px-6 py-4" colspan="4">{{__('No users found.')}}</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="contacts.links" />

    <div
      v-if="passwordModal.open"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      @click.self="closePasswordModal"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6" role="dialog" aria-modal="true">
        <h2 class="text-lg font-bold text-gray-900 mb-2">{{ __('Reset Password') }}</h2>
        <p v-if="passwordModal.contact" class="text-sm text-gray-600 mb-6">
          {{ passwordModal.contact.name }} — {{ passwordModal.contact.email }}
        </p>
        <form @submit.prevent="submitPassword">
          <text-input v-model="passwordForm.password" :error="passwordForm.errors.password" class="mb-4" type="password" autocomplete="new-password" :label="__('Password')" />
          <text-input v-model="passwordForm.password_confirmation" :error="passwordForm.errors.password_confirmation" class="mb-6" type="password" autocomplete="new-password" :label="__('Confirm Password')" />
          <div class="flex justify-end gap-3">
            <button type="button" class="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50" @click="closePasswordModal">{{ __('Cancel') }}</button>
            <loading-button :loading="passwordForm.processing" class="btn-indigo" type="submit">{{ __('Save password') }}</loading-button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'
import axios from 'axios'

export default {
  metaInfo: { title: 'Users' },
  components: {
    Icon,
    LoadingButton,
    Pagination,
    SearchFilter,
    TextInput,
  },
  layout: Layout,
  props: {
    filters: Object,
    contacts: Object,
  },
  computed: {
    isOwnerAccount() {
      const u = this.$page.props.auth.user
      return Boolean(u && u.owner)
    },
  },
  data() {
    return {
      form: {
        search: this.filters.search,
        trashed: this.filters.trashed,
      },
      passwordModal: {
        open: false,
        contact: null,
      },
      passwordForm: this.$inertia.form({
        password: '',
        password_confirmation: '',
      }),
      inviteFeedback: null,
      inviteFeedbackTimer: null,
    }
  },
  beforeDestroy() {
    if (this.inviteFeedbackTimer) {
      clearTimeout(this.inviteFeedbackTimer)
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(this.route('contacts'), pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    clearInviteFeedback() {
      if (this.inviteFeedbackTimer) {
        clearTimeout(this.inviteFeedbackTimer)
        this.inviteFeedbackTimer = null
      }
      this.inviteFeedback = null
    },
    setInviteFeedback(type, message) {
      this.clearInviteFeedback()
      this.inviteFeedback = { type, message }
      this.inviteFeedbackTimer = setTimeout(() => {
        this.inviteFeedback = null
        this.inviteFeedbackTimer = null
      }, 8000)
    },
    inviteErrorMessage(error) {
      const d = error.response && error.response.data
      if (!d) {
        return this.__('Something went wrong.')
      }
      if (d.message) {
        return d.message
      }
      if (d.errors && d.errors.id) {
        return Array.isArray(d.errors.id) ? d.errors.id[0] : d.errors.id
      }
      return this.__('Something went wrong.')
    },
    csrfToken() {
      const fromPage = this.$page.props.csrf_token
      if (fromPage) {
        return fromPage
      }
      const m = document.head.querySelector('meta[name="csrf-token"]')
      return m && m.content ? m.content : ''
    },
    sendJoinEmail(contactId) {
      if (!window.confirm(this.__('Confirm send invite to this user?'))) {
        return
      }
      const token = this.csrfToken()
      axios
        .post(
          this.route('joinonowsingle'),
          { id: contactId, _token: token },
          {
            headers: {
              'X-CSRF-TOKEN': token,
              'X-Requested-With': 'XMLHttpRequest',
            },
          },
        )
        .then((response) => {
          if (response.data && response.data.success) {
            this.setInviteFeedback('success', response.data.message)
          } else if (response.data && response.data.message) {
            this.setInviteFeedback('error', response.data.message)
          }
        })
        .catch((error) => {
          this.setInviteFeedback('error', this.inviteErrorMessage(error))
        })
    },
    sendJoinEmailAll() {
      if (!window.confirm(this.__('Confirm send invite to all users?'))) {
        return
      }
      axios
        .get(this.route('joinonow'))
        .then((response) => {
          if (response.data && response.data.success) {
            this.setInviteFeedback('success', response.data.message)
          } else if (response.data && response.data.message) {
            this.setInviteFeedback('error', response.data.message)
          }
        })
        .catch((error) => {
          this.setInviteFeedback('error', this.inviteErrorMessage(error))
        })
    },
    openPasswordModal(contact) {
      this.passwordModal = { open: true, contact }
      this.passwordForm.reset()
      this.passwordForm.clearErrors()
    },
    closePasswordModal() {
      this.passwordModal = { open: false, contact: null }
      this.passwordForm.reset()
      this.passwordForm.clearErrors()
    },
    submitPassword() {
      if (!this.passwordModal.contact) {
        return
      }
      this.passwordForm.put(this.route('owner.contacts.user-password.update', this.passwordModal.contact.id), {
        preserveScroll: true,
        onSuccess: () => this.closePasswordModal(),
      })
    },
  },
}
</script>
